// src/pages/userManagement/userList/userList.js
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import dayjs from "dayjs";
import { Button, DatePicker, Modal, Pagination, Table, Tooltip } from "antd";
import {
  FaCheck,
  FaTrash,
  FaTimes
  // FaLock, FaLockOpen
} from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./userList.css";

const { RangePicker } = DatePicker;

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  users: [],
  currentlyEditingId: null,
  currentPage: 1,
  pageSize: 10,
  sortConfig: null,
  showDateRangeModal: false,
  tempFromDate: null,
  tempToDate: null,
  fromDate: null,
  toDate: null,
  showApproveModal: false,
  selectedUserId: null,
  isApproving: false,
  showBlockModal: false,
  blockUserId: null,
  isBlocking: false
};

// Reducer for state management
const userReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setUsers":
      return { ...state, users: action.payload };
    case "setEditingId":
      return { ...state, currentlyEditingId: action.payload };
    case "updateUserApproval":
      return {
        ...state,
        users: state.users.map((user) => (user.userId === action.payload.userId ? { ...user, isAdminApproved: action.payload.isAdminApproved } : user))
      };
    case "updateUserBlock":
      return {
        ...state,
        users: state.users.map((user) => (user.userId === action.payload.userId ? { ...user, isBlocked: action.payload.isBlocked } : user))
      };
    case "updateUserRole":
      return {
        ...state,
        users: state.users.map((user) => (user.userId === action.payload.userId ? { ...user, role: action.payload.role } : user))
      };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload, currentPage: 1 };
    case "setDateRangeModal":
      return { ...state, showDateRangeModal: action.payload };
    case "setTempDateRange":
      return {
        ...state,
        tempFromDate: action.payload.from,
        tempToDate: action.payload.to
      };
    case "setDateRange":
      return {
        ...state,
        fromDate: action.payload.from,
        toDate: action.payload.to
      };
    case "setApproveModal":
      return {
        ...state,
        showApproveModal: action.payload.show,
        selectedUserId: action.payload.userId || null
      };
    case "setIsApproving":
      return { ...state, isApproving: action.payload };
    case "setBlockModal":
      return {
        ...state,
        showBlockModal: action.payload.show,
        blockUserId: action.payload.userId || null
      };
    case "setIsBlocking":
      return { ...state, isBlocking: action.payload };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1,
        fromDate: null,
        toDate: null,
        tempFromDate: null,
        tempToDate: null,
        showDateRangeModal: false
      };
    default:
      return state;
  }
};

function UserList() {
  const { notification, showNotification, hideNotification } = useNotification();
  const dropdownRef = useRef(null);
  const [state, dispatch] = useReducer(userReducer, initialState);

  // State for immediate search display
  const [displaySearchTerm, setDisplaySearchTerm] = useState("");
  const [unpinnedIds, setUnpinnedIds] = useState(new Set());

  const {
    isLoading,
    searchTerm,
    filters,
    users,
    currentlyEditingId,
    currentPage,
    pageSize,
    sortConfig,
    showDateRangeModal,
    tempFromDate,
    tempToDate,
    fromDate,
    toDate,
    showApproveModal,
    selectedUserId,
    isApproving,
    showBlockModal,
    blockUserId,
    isBlocking
  } = state;

  // Helper function for activity status
  const getLastActiveTime = useCallback((lastFlutterLoginAt) => {
    if (!lastFlutterLoginAt) return "No Data";

    const now = new Date();
    const loginTime = new Date(lastFlutterLoginAt);
    const diffInMinutes = Math.floor((now - loginTime) / (1000 * 60));

    if (diffInMinutes < 1) return "Active now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  }, []);

  // Get activity status based on lastFlutterLoginAt
  const getActivityStatus = useCallback((lastFlutterLoginAt) => {
    if (!lastFlutterLoginAt) return "Inactive User";

    const now = new Date();
    const loginTime = new Date(lastFlutterLoginAt);
    const diffInDays = Math.floor((now - loginTime) / (1000 * 60 * 60 * 24));

    if (diffInDays <= 7) return "Active User";
    return "Inactive User";
  }, []);

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      registrationDate: {
        label: "Registration Date",
        options: [
          { value: "last7Days", label: "Last 7 Days" },
          { value: "last30Days", label: "Last 30 Days" },
          { value: "dateRange", label: "Custom Date Range" }
        ]
      },
      activityStatus: {
        label: "Activity Status",
        options: [
          { value: "Active User", label: "Active User" },
          { value: "Inactive User", label: "Inactive User" }
        ]
      },
      approvalStatus: {
        label: "Approval Status",
        options: [
          { value: "approved", label: "Approved" },
          { value: "notApproved", label: "Not Approved" }
        ]
      }
    }),
    []
  );

  // Date filtering utility
  const filterByDate = useCallback(
    (items, dateFilter) => {
      if (!dateFilter) return items;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (dateFilter) {
        case "last7Days": {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return items.filter((item) => new Date(item.createdAt) >= sevenDaysAgo);
        }
        case "last30Days": {
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return items.filter((item) => new Date(item.createdAt) >= thirtyDaysAgo);
        }
        case "dateRange": {
          if (fromDate && toDate) {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            return items.filter((item) => {
              const date = new Date(item.createdAt);
              return date >= from && date <= to;
            });
          }
          return items;
        }
        default:
          return items;
      }
    },
    [fromDate, toDate]
  );

  // Global search function
  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;

    const searchLower = term.toLowerCase().trim();

    return items.filter((item) => {
      const searchFields = [
        item.name || "",
        item.email || "",
        item.phoneNumber || "",
        item.createdAt
          ? new Date(item.createdAt)
            .toLocaleDateString("en-GB", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "short",
              year: "numeric"
            })
            .replace(/ /g, "-")
          : ""
      ];

      return searchFields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  // Apply all filters, search, and sorting in correct order (sorting BEFORE pagination)
  const filteredData = useMemo(() => {
    let result = [...users];

    // Step 1: Apply date filter
    result = filterByDate(result, filters.registrationDate);

    // Step 2: Apply activity status filter
    if (filters.activityStatus) {
      result = result.filter((item) => {
        const status = getActivityStatus(item.lastFlutterLoginAt);
        return status === filters.activityStatus;
      });
    }

    // Step 3: Apply approval status filter
    if (filters.approvalStatus) {
      if (filters.approvalStatus === "approved") {
        result = result.filter((user) => user.isAdminApproved === true);
      } else if (filters.approvalStatus === "notApproved") {
        result = result.filter((user) => user.isAdminApproved === false || user.isAdminApproved === undefined);
      }
    }

    // Step 4: Apply global search
    result = globalSearch(result, searchTerm);

    // Step 5: Apply sorting BEFORE pagination
    if (sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;

        if (key === "createdAt") {
          const dateA = new Date(a.createdAt);
          const dateB = new Date(b.createdAt);
          return direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        if (key === "isMember") {
          const valA = a.isMember ? 1 : 0;
          const valB = b.isMember ? 1 : 0;
          return direction === "asc" ? valA - valB : valB - valA;
        }

        if (key === "lastFlutterLoginAt") {
          const dateA = a.lastFlutterLoginAt ? new Date(a.lastFlutterLoginAt) : new Date(0);
          const dateB = b.lastFlutterLoginAt ? new Date(b.lastFlutterLoginAt) : new Date(0);
          return direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";

        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    // Step 6: Pin head admin + no member rows to the top (unless unpinned)
    result.sort((a, b) => {
      const aFlag = a.isHeadAdmin && !a.isMember && !unpinnedIds.has(a.userId) ? 1 : 0;
      const bFlag = b.isHeadAdmin && !b.isMember && !unpinnedIds.has(b.userId) ? 1 : 0;
      return bFlag - aFlag;
    });

    return result;
  }, [users, filters, searchTerm, filterByDate, globalSearch, sortConfig, getActivityStatus, unpinnedIds]);

  // Get current page data (pagination happens AFTER all processing)
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Event handlers
  const openApproveModal = useCallback((userId, e) => {
    e.stopPropagation();
    dispatch({ type: "setApproveModal", payload: { show: true, userId } });
  }, []);

  // const openBlockModal = useCallback((userId, e) => {
  //   e.stopPropagation();
  //   dispatch({ type: 'setBlockModal', payload: { show: true, userId } });
  // }, []);

  // Confirm approve/unapprove action
  const confirmApprove = useCallback(async () => {
    if (!selectedUserId) return;

    dispatch({ type: "setIsApproving", payload: true });

    try {
      const token = await getAuthToken();
      const user = users.find((u) => u.userId === selectedUserId);
      const newStatus = !user.isAdminApproved;

      const response = await axios.put(
        `${API_ENDPOINTS.USERS}/${selectedUserId}`,
        { isAdminApproved: newStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        dispatch({
          type: "updateUserApproval",
          payload: { userId: selectedUserId, isAdminApproved: newStatus }
        });
        showNotification(newStatus ? "User approved successfully!" : "User approval removed successfully!", newStatus ? "success" : "warning");
        dispatch({ type: "setApproveModal", payload: { show: false } });
      } else {
        showNotification(response.data?.message || "Failed to update user approval.", "error");
      }
    } catch (error) {
      console.error("Approval error:", error.response?.data || error.message);
      showNotification(error.response?.data?.message || "Failed to update user approval.", "error");
    } finally {
      dispatch({ type: "setIsApproving", payload: false });
    }
  }, [selectedUserId, users, showNotification]);

  // Confirm block/unblock action
  const confirmBlock = useCallback(async () => {
    if (!blockUserId) return;

    dispatch({ type: "setIsBlocking", payload: true });

    try {
      const token = await getAuthToken();
      const user = users.find((u) => u.userId === blockUserId);
      const newBlockStatus = !user.isBlocked;

      const response = await axios.put(
        `${API_ENDPOINTS.USERS}/${blockUserId}`,
        { isBlocked: newBlockStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        dispatch({
          type: "updateUserBlock",
          payload: { userId: blockUserId, isBlocked: newBlockStatus }
        });
        showNotification(newBlockStatus ? "User blocked successfully!" : "User unblocked successfully!", newBlockStatus ? "warning" : "success");
        dispatch({ type: "setBlockModal", payload: { show: false } });
      } else {
        showNotification(response.data?.message || "Failed to update user block status.", "error");
      }
    } catch (error) {
      console.error("Block error:", error.response?.data || error.message);
      showNotification(error.response?.data?.message || "Failed to update user block status.", "error");
    } finally {
      dispatch({ type: "setIsBlocking", payload: false });
    }
  }, [blockUserId, users, showNotification]);

  // Debounced search dispatch
  const debouncedDispatch = useMemo(
    () =>
      debounce((term) => {
        dispatch({ type: "setSearchTerm", payload: term });
      }, 300),
    []
  );

  const handleSearch = useCallback(
    (term) => {
      setDisplaySearchTerm(term);
      debouncedDispatch(term);
    },
    [debouncedDispatch]
  );

  const handleFilterChange = useCallback(
    (newFilters) => {
      if (newFilters.registrationDate === "dateRange" && filters.registrationDate !== "dateRange") {
        dispatch({ type: "setDateRangeModal", payload: true });
        return;
      }

      if (filters.registrationDate === "dateRange" && newFilters.registrationDate !== "dateRange") {
        dispatch({
          type: "setDateRange",
          payload: { from: null, to: null }
        });
        dispatch({
          type: "setTempDateRange",
          payload: { from: null, to: null }
        });
      }

      dispatch({ type: "setFilters", payload: newFilters });
    },
    [filters]
  );

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
    setDisplaySearchTerm("");
    setUnpinnedIds(new Set());
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  const handleDateRangeChange = useCallback((dates, dateStrings) => {
    dispatch({
      type: "setTempDateRange",
      payload: {
        from: dateStrings[0] || null,
        to: dateStrings[1] || null
      }
    });
  }, []);

  const applyDateRangeFilter = useCallback(() => {
    if (tempFromDate && tempToDate) {
      dispatch({
        type: "setDateRange",
        payload: { from: tempFromDate, to: tempToDate }
      });
      dispatch({
        type: "setFilters",
        payload: { ...filters, registrationDate: "dateRange" }
      });
      dispatch({ type: "setDateRangeModal", payload: false });
    } else {
      alert("Please select both from and to dates");
    }
  }, [tempFromDate, tempToDate, filters]);

  const handleDateRangeModalCancel = useCallback(() => {
    dispatch({ type: "setDateRangeModal", payload: false });
    dispatch({
      type: "setTempDateRange",
      payload: { from: null, to: null }
    });
    const updatedFilters = { ...filters };
    delete updatedFilters.registrationDate;
    dispatch({ type: "setFilters", payload: updatedFilters });
  }, [filters]);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    if (sorter.order) {
      dispatch({
        type: "setSortConfig",
        payload: {
          key: sorter.field,
          direction: sorter.order === "ascend" ? "asc" : "desc"
        }
      });
    } else {
      dispatch({
        type: "setSortConfig",
        payload: null
      });
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "FULL NAME",
        dataIndex: "name",
        sorter: true,
        sortOrder: sortConfig?.key === "name" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (_, record) => (
          <div className="usl-name-email">
            <div className="usl-name">{record.name || "-"}</div>
            <div className="usl-email">{record.email || "-"}</div>
          </div>
        )
      },
      {
        title: "PHONE NUMBER",
        dataIndex: "phoneNumber",
        render: (phone) => phone || "-"
      },
      {
        title: "REGISTRATION DATE",
        dataIndex: "createdAt",
        sorter: true,
        sortOrder: sortConfig?.key === "createdAt" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (date) =>
          date
            ? new Date(date)
              .toLocaleDateString("en-GB", {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "short",
                year: "numeric"
              })
              .replace(/ /g, "-")
            : "-"
      },
      {
        title: "MEMBER",
        dataIndex: "isMember",
        sorter: true,
        sortOrder: sortConfig?.key === "isMember" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (isMember) => (isMember ? "Yes" : "No")
      },
      {
        title: "ACTIVITY STATUS",
        dataIndex: "lastFlutterLoginAt",
        sorter: true,
        sortOrder: sortConfig?.key === "lastFlutterLoginAt" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (lastFlutterLoginAt, record) => (
          <div className="usl-login-status">
            <span>
              {lastFlutterLoginAt
                ? new Date(lastFlutterLoginAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric"
                })
                : "Inactive"}
            </span>
            <span className={`usl-status-text ${getLastActiveTime(lastFlutterLoginAt) === "Active now" ? "active" : ""}`}>{getLastActiveTime(lastFlutterLoginAt)}</span>
          </div>
        )
      },
      {
        title: "ACTIONS",
        render: (_, record) => (
          <div className="usl-action-buttons" onClick={(e) => e.stopPropagation()}>
            <Tooltip title={record.isAdminApproved ? "Unapprove" : "Approve"}>
              <button
                className={`usl-table-approve-btn ${record.isAdminApproved ? "approved" : ""}`}
                disabled={record.isBlocked}
                onClick={(e) => openApproveModal(record.userId, e)}
              >
                {record.isAdminApproved ? <FaTimes /> : <FaCheck />}
              </button>
            </Tooltip>
            <Tooltip title="Delete">
              <button className="usl-table-delete-btn" disabled>
                <FaTrash />
              </button>
            </Tooltip>
            {/* DO NOT REMOVE THE FOLLOWING CODE */}
            {/* <Tooltip title={record.isBlocked ? "Unblock" : "Block"}>
            <button
              className={`usl-table-block-btn ${record.isBlocked ? "unblocked" : ""}`}
              onClick={(e) => openBlockModal(record.userId, e)}
            >
              {record.isBlocked ? <FaLockOpen /> : <FaLock />}
            </button>
          </Tooltip> */}
          </div>
        ),
        width: 200
      }
    ],
    [currentPage, pageSize, sortConfig, openApproveModal, getLastActiveTime]
  );

  const prepareExcelData = useCallback(() => {
    return filteredData.map((user) => ({
      "User ID": user.userId,
      "Full Name": user.name,
      Email: user.email,
      "Phone Number": user.phoneNumber,
      Member: user.isMember ? "Yes" : "No",
      "Activity Status": getLastActiveTime(user.lastFlutterLoginAt),
      "Registration Date": user.createdAt,
      "Approval Status": user.isAdminApproved ? "Approved" : "Not Approved"
    }));
  }, [filteredData, getLastActiveTime]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        dispatch({ type: "setEditingId", payload: null });
      }
    };

    if (currentlyEditingId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [currentlyEditingId]);

  useEffect(() => {
    let isMounted = true;

    const fetchUsers = async () => {
      try {
        dispatch({ type: "setLoading", payload: true });

        const token = await getAuthToken();

        const response = await axios.get(`${API_ENDPOINTS.USERS}?status=signedup`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (isMounted) {
          dispatch({
            type: "setUsers",
            payload: response.data.users || []
          });

          (response.data.users || []).forEach((user) => {
            console.log(`userId: ${user.userId} | isMember: ${user.isMember}`);
          });
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        if (isMounted) {
          showNotification("Failed to load users", "error");
        }
      } finally {
        if (isMounted) {
          dispatch({ type: "setLoading", payload: false });
        }
      }
    };

    fetchUsers();

    return () => {
      isMounted = false;
    };
  }, [showNotification]);

  return (
    <Layout
      navTitle="User Management"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search across all fields..."
            searchTerm={displaySearchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="user-list"
            excelSheetName="Users"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.userId}
          rowClassName={(record, index) => {
            const baseClass = index % 2 === 0 ? "table-row-light" : "table-row-dark";
            if (record.isBlocked) return `${baseClass} blocked-row`;
            if (record.isHeadAdmin && !record.isMember) return `${baseClass} head-admin-no-member-row`;
            return baseClass;
          }}
          components={{
            body: {
              row: ({ children, className, ...restProps }) => {
                const record = currentPageData.find(
                  (r) => r.userId === restProps["data-row-key"]
                );
                const isPinned = record?.isHeadAdmin && !record?.isMember && !unpinnedIds.has(record?.userId);
                const showWarning = record?.isHeadAdmin && !record?.isMember;

                return (
                  <Tooltip
                    title={showWarning ? "This user is a Head Admin however has not filled Membership form or it is not approved." : ""}
                    placement="top"
                  >
                    <tr className={className} {...restProps} style={{ position: "relative" }}>
                      {children}
                      {isPinned && (
                        <td
                          style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", padding: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setUnpinnedIds((prev) => new Set([...prev, record.userId]));
                          }}
                        >
                          <Tooltip title="Unpin row">
                            <span style={{ cursor: "pointer", fontSize: 12, color: "#aaa" }}>📌 Unpin</span>
                          </Tooltip>
                        </td>
                      )}
                    </tr>
                  </Tooltip>
                );
              }
            }
          }}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{ showExpandColumn: false }}
          onChange={handleTableChange}
        />
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      <Modal
        title="Select Date Range"
        open={showDateRangeModal}
        onCancel={handleDateRangeModalCancel}
        footer={[
          <Button key="cancel" onClick={handleDateRangeModalCancel}>
            Cancel
          </Button>,
          <Button key="apply" onClick={applyDateRangeFilter}>
            Apply Filter
          </Button>
        ]}
        width={450}
        centered={false}
        style={{ top: 120 }}
      >
        <div style={{ marginBottom: "20px" }}>
          <RangePicker
            style={{ width: "100%" }}
            value={tempFromDate && tempToDate ? [dayjs(tempFromDate), dayjs(tempToDate)] : []}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            placeholder={["From Date", "To Date"]}
          />
        </div>
      </Modal>

      <Modal
        title={users.find((u) => u.userId === selectedUserId)?.isAdminApproved ? "Remove Approval" : "Confirm Approval"}
        open={showApproveModal}
        onCancel={() => dispatch({ type: "setApproveModal", payload: { show: false } })}
        footer={[
          <Button className="usl-modal-cancel-btn" key="cancel" onClick={() => dispatch({ type: "setApproveModal", payload: { show: false } })}>
            Cancel
          </Button>,
          <Button className="usl-modal-confirm-btn" key="confirm" loading={isApproving} onClick={confirmApprove}>
            Confirm
          </Button>
        ]}
        centered
      >
        <p>{users.find((u) => u.userId === selectedUserId)?.isAdminApproved ? "Do you want to unapprove this user?" : "Are you sure you want to approve this user?"}</p>
      </Modal>

      <Modal
        title={users.find((u) => u.userId === blockUserId)?.isBlocked ? "Unblock User" : "Block User"}
        open={showBlockModal}
        onCancel={() => dispatch({ type: "setBlockModal", payload: { show: false } })}
        footer={[
          <Button className="usl-modal-cancel-btn" key="cancel" onClick={() => dispatch({ type: "setBlockModal", payload: { show: false } })}>
            Cancel
          </Button>,
          <Button className="usl-modal-delete-btn" key="confirm" loading={isBlocking} onClick={confirmBlock}>
            Confirm
          </Button>
        ]}
        centered
      >
        <p>
          {users.find((u) => u.userId === blockUserId)?.isBlocked
            ? "Do you want to unblock this user? They will regain access to their account."
            : "Are you sure you want to block this user? This will disable their account and prevent them from accessing the system."}
        </p>
      </Modal>
    </Layout>
  );
}

export default UserList;